import { __assign, __extends } from "tslib";
import React from 'react';
import throttle from 'lodash/throttle';
import isEqual from 'deep-eql';
import { withMobileContext, } from '@wix/da-react-context/pkg/MobileContext';
export var DEFAULT_SIZE = {
    desktop: { width: 1024, height: 768 },
    mobile: { width: 375, height: 667 },
};
var THROTTLE_TIMEOUT = 500;
var THROTTLE_OPTIONS = { trailing: true };
var Measured = /** @class */ (function (_super) {
    __extends(Measured, _super);
    function Measured() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.sentinalRef = React.createRef();
        _this.mounted = false;
        // For client-side we can skip first deviations rendering,
        // when we don`t know real dimensions to speed-up first rendering
        _this.state = {
            container: _this.getInitialStateContainer(),
        };
        _this.saveRefAndUpdateContainer = function (node) {
            if (!node) {
                return;
            }
            _this.containerEl = node;
            // Need to wait for mount to happen, so schedule for next frame
            // because this is called before mount because its a child ref
            // this fixes react warnings about setState on unMounted component.
            requestAnimationFrame(function () { return _this.updateContainerSize(); });
        };
        _this.updateContainerSize = throttle(function () {
            if (_this.isServerRendering() || _this.mounted === false) {
                return;
            }
            var container = _this.state.container;
            var newContainer = _this.getContainer();
            var sentinalWidth = _this.getSentinalWidth();
            // Make sure container is never bigger than parent
            // this allows us to shrink the container :)
            if (_this.props.limitToParentWidth &&
                sentinalWidth &&
                sentinalWidth < newContainer.width) {
                newContainer = __assign(__assign({}, newContainer), { width: sentinalWidth });
            }
            if (isEqual(container, newContainer)) {
                return;
            }
            _this.setState({ container: newContainer });
        }, THROTTLE_TIMEOUT, THROTTLE_OPTIONS);
        return _this;
    }
    Measured.prototype.getInitialStateContainer = function () {
        if (this.props.limitToParentWidth) {
            return this.getSSRContainer();
        }
        return this.isServerRendering() ? this.getSSRContainer() : undefined;
    };
    Measured.prototype.componentDidMount = function () {
        var _a = this.props, listenOnWindow = _a.listenOnWindow, resizeHandler = _a.resizeHandler;
        this.mounted = true;
        if (listenOnWindow) {
            this.windowResizeListener = resizeHandler
                ? throttle(resizeHandler, THROTTLE_TIMEOUT, THROTTLE_OPTIONS)
                : this.updateContainerSize;
            window.addEventListener('resize', this.windowResizeListener);
        }
        this.updateContainerSize();
    };
    Measured.prototype.componentDidUpdate = function (prevProps, _a) {
        var prevContainer = _a.container;
        var container = this.state.container;
        var _b = this.props, onUpdatedDims = _b.onUpdatedDims, _c = _b.cookieDim, cookieDim = _c === void 0 ? 'both' : _c;
        if (!container || !onUpdatedDims) {
            return;
        }
        var widthChanged = ['width', 'both'].includes(cookieDim) &&
            (!prevContainer || container.width !== prevContainer.width);
        var heightChanged = ['height', 'both'].includes(cookieDim) &&
            (!prevContainer || container.height !== prevContainer.height);
        if (widthChanged || heightChanged) {
            onUpdatedDims(container);
        }
    };
    Measured.prototype.componentWillUnmount = function () {
        if (this.windowResizeListener) {
            window.removeEventListener('resize', this.windowResizeListener);
        }
        this.mounted = false;
    };
    Measured.prototype.isServerRendering = function () {
        return typeof document === 'undefined' || typeof window === 'undefined';
    };
    Measured.prototype.getDefaultSize = function () {
        var _a = this.props, defaultSize = _a.defaultSize, isMobile = _a.isMobile;
        if (isMobile) {
            return (defaultSize || DEFAULT_SIZE).mobile;
        }
        return (defaultSize || DEFAULT_SIZE).desktop;
    };
    Measured.prototype.getSSRContainer = function () {
        var prevLoadDims = this.props.prevLoadDims;
        if (prevLoadDims && (prevLoadDims.width > 0 || prevLoadDims.height > 0)) {
            return prevLoadDims;
        }
        return this.getDefaultSize();
    };
    Measured.prototype.getContainer = function () {
        // We are in browser from here
        if (this.props.measureWindow) {
            return {
                width: window.innerWidth,
                height: window.innerHeight,
            };
        }
        // If the child component did not use our onRef, we use default size
        // This can be a slight optimisation in that we dont always measure a DOM el.
        if (!this.containerEl || !this.containerEl.getBoundingClientRect) {
            return this.getDefaultSize();
        }
        // We have specific element to measure
        var rect = this.containerEl.getBoundingClientRect();
        if (!rect.width && !rect.height) {
            return this.getDefaultSize();
        }
        return {
            width: rect.width,
            height: rect.height,
        };
    };
    Measured.prototype.getSentinalWidth = function () {
        if (!this.sentinalRef.current) {
            return undefined;
        }
        return this.sentinalRef.current.getBoundingClientRect().width;
    };
    Measured.prototype.render = function () {
        var container = this.state.container;
        if (this.props.limitToParentWidth) {
            return (React.createElement(React.Fragment, null,
                React.createElement("div", { ref: this.sentinalRef, style: { maxWidth: '100%' } }),
                this.props.children({
                    container: container,
                    onRef: this.saveRefAndUpdateContainer,
                })));
        }
        return this.props.children({
            container: container,
            onRef: this.saveRefAndUpdateContainer,
        });
    };
    return Measured;
}(React.Component));
export { Measured };
/** @deprecated */
export default withMobileContext(Measured);
//# sourceMappingURL=Measured.js.map